import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { setParams } from '../config/core.httpclient-utils';

export interface RetryPolicy {
    max_attempts: number;
    backoff_seconds: number[];
}

export interface EventDefinition {
    type: string;
    category: string;
    entity: string;
    action: string;
    description: string;
    details: string;
    volume: string;
    placeholders: string[];
    default_retry_policy: RetryPolicy;
    scope: string;
}

export interface WebhookActionConfig {
    url: string;
    method?: string;
}

export interface EmailActionConfig {
    recipients: string[];
    subject_template: string;
    body_template: string;
}

export interface EventSubscription {
    id: number;
    name: string;
    enabled: boolean;
    event_pattern: string;
    action_type: string;
    action_config: WebhookActionConfig | EmailActionConfig | null;
    retry_policy: RetryPolicy | null;
    retention_days: number;
    project_id: number | null;
    owner_user_id: number | null;
    created_at: string | null;
    updated_at: string | null;
}

export interface EventDelivery {
    id: number;
    subscription_id: number | null;
    event_type: string;
    action_type: string;
    status: string;
    last_response_code: number | null;
    scheduled_at: string | null;
    finalized_at: string | null;
    completed_in: number | null;
    envelope: any | null;
    last_response_excerpt: string | null;
}

export interface SaveSubscriptionResult {
    location: string;
    secret?: string;
}

export interface RegenerateSecretResult extends EventSubscription {
    secret: string;
}

@Injectable()
export class EventWebhookService {

    constructor(private http: HttpClient) {}

    getEventCatalog(filters: { category?: string; entity?: string } = {}): Promise<any> {
        return this.http.get<any>('/api/event', { params: setParams(filters) }).toPromise();
    }

    getEventDefinition(type: string): Promise<EventDefinition> {
        return this.http.get<EventDefinition>(`/api/event/${encodeURIComponent(type)}`).toPromise();
    }

    listSubscriptions(filters: { project_id?: number | string; page_num?: number; page_size?: number } = {}): Promise<any> {
        return this.http.get<any>('/api/eventsubscription', { params: setParams(filters) }).toPromise();
    }

    getSubscription(id: number): Promise<EventSubscription> {
        return this.http.get<EventSubscription>(`/api/eventsubscription/${id}`).toPromise();
    }

    saveSubscription(data: any): Promise<SaveSubscriptionResult> {
        const id = data.id || 0;
        const url = id > 0 ? `/api/eventsubscription/${id}` : '/api/eventsubscription';
        return this.http.post<SaveSubscriptionResult>(url, data).toPromise();
    }

    regenerateSecret(id: number): Promise<RegenerateSecretResult> {
        return this.http.post<RegenerateSecretResult>(`/api/eventsubscription/${id}/secret`, {}).toPromise();
    }

    deleteSubscription(id: number): Promise<any> {
        return this.http.delete(`/api/eventsubscription/${id}`).toPromise();
    }

    listDeliveries(filters: {
        subscription_id?: number | string;
        event_type?: string;
        status?: string;
        date_start?: string;
        date_end?: string;
        sort_field?: string;
        sort_dir?: string;
        page_num?: number;
        page_size?: number;
    } = {}): Promise<any> {
        return this.http.get<any>('/api/eventdelivery', { params: setParams(filters) }).toPromise();
    }

    getDelivery(id: number): Promise<EventDelivery> {
        return this.http.get<EventDelivery>(`/api/eventdelivery/${id}`).toPromise();
    }
}
